import React, { Suspense, lazy } from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import { Toaster } from "./components/ui/sonner";
import { ErrorBoundary } from "./components/ErrorBoundary";
import CookieConsent from "./components/CookieConsent";
import LandingPage from "./pages/LandingPage";
import { PHOTOS_ENABLED } from "./lib/featureFlags";

// Lazy-loaded routes — only LandingPage is in the initial bundle
const OnboardingPage = lazy(() => import("./pages/OnboardingPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const PeoplePage = lazy(() => import("./pages/PeoplePage"));
const PlacesPage = lazy(() => import("./pages/PlacesPage"));
const SearchPage = lazy(() => import("./pages/SearchPage"));
const RelationshipWebPage = lazy(() => import("./pages/RelationshipWebPage"));
const ReportPage = lazy(() => import("./pages/ReportPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const SubscriptionSuccessPage = lazy(() => import("./pages/SubscriptionSuccessPage"));
const RedeemPage = lazy(() => import("./pages/RedeemPage"));
const GiftSuccessPage = lazy(() => import("./pages/GiftSuccessPage"));
// PhotosPage import is conditional on PHOTOS_ENABLED. When disabled the
// /photos route redirects to /dashboard so the chunk isn't requested.
const PhotosPage = PHOTOS_ENABLED ? lazy(() => import("./pages/PhotosPage")) : null;
const CollaboratorsPage = lazy(() => import("./pages/CollaboratorsPage"));
const TimelinePage = lazy(() => import("./pages/TimelinePage"));
const RespondPage = lazy(() => import("./pages/RespondPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const SharePage = lazy(() => import("./pages/SharePage"));
const SharedTimelinePage = lazy(() => import("./pages/SharedTimelinePage"));
const ValuesPage = lazy(() => import("./pages/ValuesPage"));
const ValuesMapPage = lazy(() => import("./pages/ValuesMapPage"));
// PortraitPage retained on disk for data/i18n reuse but the route is
// collapsed into /values. No lazy import here — the component is no
// longer mounted directly anywhere. Re-add the import if a standalone
// route is reinstated.
const WeeklyPage = lazy(() => import("./pages/WeeklyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const ImprintPage = lazy(() => import("./pages/ImprintPage"));
const CookiesPage = lazy(() => import("./pages/CookiesPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));

import { LogoAnimated } from "./components/Logo";

function PageLoader() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="loader-brand">
        <LogoAnimated size={56} />
        <span className="loader-brand-text">Loading</span>
      </div>
    </div>
  );
}

// Protected Route Component
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function withSuspense(Component) {
  return <Suspense fallback={<PageLoader />}>{Component}</Suspense>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/onboarding" element={withSuspense(<ProtectedRoute><OnboardingPage /></ProtectedRoute>)} />
      <Route path="/dashboard" element={withSuspense(<ProtectedRoute><DashboardPage /></ProtectedRoute>)} />
      <Route path="/people" element={withSuspense(<ProtectedRoute><PeoplePage /></ProtectedRoute>)} />
      <Route path="/places" element={withSuspense(<ProtectedRoute><PlacesPage /></ProtectedRoute>)} />
      <Route path="/search" element={withSuspense(<ProtectedRoute><SearchPage /></ProtectedRoute>)} />
      <Route path="/relationships" element={withSuspense(<ProtectedRoute><RelationshipWebPage /></ProtectedRoute>)} />
      <Route path="/report" element={withSuspense(<ProtectedRoute><ReportPage /></ProtectedRoute>)} />
      <Route path="/pricing" element={withSuspense(<PricingPage />)} />
      <Route path="/timeline" element={withSuspense(<ProtectedRoute><TimelinePage /></ProtectedRoute>)} />
      {/*
        Photo features paused (PHOTOS_ENABLED=false in lib/featureFlags.js).
        The route stays defined so old links don't 404; it redirects to
        /dashboard until the flag flips back on.
      */}
      <Route
        path="/photos"
        element={PHOTOS_ENABLED && PhotosPage
          ? withSuspense(<ProtectedRoute><PhotosPage /></ProtectedRoute>)
          : <Navigate to="/dashboard" replace />}
      />
      <Route path="/collaborators" element={withSuspense(<ProtectedRoute><CollaboratorsPage /></ProtectedRoute>)} />
      <Route path="/subscription/success" element={withSuspense(<ProtectedRoute><SubscriptionSuccessPage /></ProtectedRoute>)} />
      <Route path="/redeem" element={withSuspense(<RedeemPage />)} />
      <Route path="/gift/success" element={withSuspense(<ProtectedRoute><GiftSuccessPage /></ProtectedRoute>)} />
      <Route path="/respond/:token" element={withSuspense(<RespondPage />)} />
      <Route path="/shared/:token" element={withSuspense(<SharedTimelinePage />)} />
      <Route path="/profile" element={withSuspense(<ProtectedRoute><ProfilePage /></ProtectedRoute>)} />
      <Route path="/privacy" element={withSuspense(<PrivacyPage />)} />
      <Route path="/terms" element={withSuspense(<TermsPage />)} />
      <Route path="/imprint" element={withSuspense(<ImprintPage />)} />
      <Route path="/cookies" element={withSuspense(<CookiesPage />)} />
      <Route path="/forgot-password" element={withSuspense(<ForgotPasswordPage />)} />
      <Route path="/reset-password" element={withSuspense(<ResetPasswordPage />)} />
      <Route path="/share" element={withSuspense(<ProtectedRoute><SharePage /></ProtectedRoute>)} />
      {/* Compass (Values) — stated vs lived gap report. NavBar shows it under
          the "compass.section" label. ValuesMapPage is the legacy dedup of
          PortraitPage and just redirects there. */}
      <Route path="/values" element={withSuspense(<ProtectedRoute><ValuesPage /></ProtectedRoute>)} />
      <Route path="/values-map" element={withSuspense(<ValuesMapPage />)} />
      {/* Legacy /memoir and /coverage routes deleted (no UI link to either, no
          backend coupling, the wildcard catch handles old bookmarks). Testament
          remains as a placeholder redirect until the portrait export flow is
          finalised. */}
      <Route path="/testament" element={<Navigate to="/dashboard" replace />} />
      {/*
        /portrait was a standalone page that duplicated functionality
        already handled by Compass. As of 2026-05-07 the Portrait
        narrative lives as a section on /values; /portrait redirects
        there so existing bookmarks and external links keep working.
        PortraitPage.js is retained on disk so the data and i18n keys
        survive — only the route is collapsed.
      */}
      <Route path="/portrait" element={<Navigate to="/values" replace />} />
      <Route path="/weekly" element={withSuspense(<ProtectedRoute><WeeklyPage /></ProtectedRoute>)} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <BrowserRouter>
          <LanguageProvider>
            <AuthProvider>
              <AppRoutes />
              <CookieConsent />
              <Toaster />
            </AuthProvider>
          </LanguageProvider>
        </BrowserRouter>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
